import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDeptStatus } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import Popover, { PopoverHeader } from '../components/ui/Popover';
import { filterBucket, healthKey, JcIcon } from '../components/jobcost/JobCostShared';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   Department Status (/dept-status) — Finance
   Budget variance by department (= CE phase) per project, plus booked-vs-current
   gross margin. Replaces the hand-maintained BOMOCO.xlsx; see
   server/routes/deptStatus.js for the recovered formulas and their verification.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Whole dollars, no cents — matching the workbook's "$"#,##0 — and parentheses
   for under-budget, which is how the finance team reads a negative already. */
const fmt$ = (n) => {
  const v = Math.round(Number(n) || 0);
  const s = Math.abs(v).toLocaleString('en-US');
  return v < 0 ? `(${s})` : s;
};
const fmt$full = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const fmtPts = (n) => (n == null ? '–' : `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(1)}`);
const fmtPct = (n) => (n == null ? '–' : `${n.toFixed(1)}%`);
// A department's or a job's variance as a signed % (one decimal), e.g. +14.6% / −22.3%.
const fmtSignedPct = (n) => (n == null ? '–' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)}%`);
// MM/DD/YY (the workbook's date format) from a 'YYYY-MM-DD', read as text: never via UTC midnight.
const fmtShortDate = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : null;
};
const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);

/* The BoMoCo workbook's highlight rules (its conditional formatting, read 2026-10-10):
   a department more than 10% over its own budget gets a red fill and more than 10% under
   goes green; Overall the same at 2% of the contract; the margin change at 2 points.
   Inside those bands the figures stay plain, as in the workbook. */
const DEPT_FLAG_PCT = 10;
const OVERALL_FLAG_PCT = 2;
const GM_FLAG_PTS = 2;
const flagOf = (pct, limit) => (pct == null ? null : pct > limit ? 'bad' : pct < -limit ? 'good' : null);
// Applied as classes (ds-bad / ds-good, see the page CSS) so they also win in dark mode.

/* The Job Costs page's filter rules, so the two Finance screens agree: health from % spent
   (Job Costs' filterBucket / healthKey), the same "Most Over Budget" order, the same sorts. */
const ratioOf = (r) => (r.budget > 0 ? r.cost / r.budget : null);
const HEALTH_RANK = { over: 0, atbudget: 1, at: 2, on: 3, under: 4, none: 5 };
const healthRank = (r) => HEALTH_RANK[healthKey(ratioOf(r))] ?? 4;
const SORT_OPTIONS = [
  { value: 'gm', label: 'Margin Change' },
  { value: 'overrun', label: 'Most Over Budget' },
  { value: 'budget', label: 'Largest Budget' },
  { value: 'spent', label: '% Spent' },
  { value: 'gm-high', label: 'Highest Gross Margin' },
  { value: 'gm-low', label: 'Lowest Gross Margin' },
  { value: 'unallocated', label: 'Unallocated' },
  { value: 'contract', label: 'Contract Size' },
  { value: 'name', label: 'Job Name' },
];

/* "How to read this page" button icon, in JcIcon's line style. */
const InfoIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 7.5h.01" />
  </svg>
);

/* Job Costs' select, so the filter bars look alike. */
const SortSelect = ({ value, onChange, options }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="ds-sort"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth: 172, fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em' }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><JcIcon name="chevron-d" size={14} /></span>
  </div>
);

/* ⚠️ Column budget, measured. A row gets `content − 38` px, i.e. ~1174 at a 1512
   viewport (see project_summit_laptop_layout: content = viewport − 300). Nine
   department columns do not fit, so the five with real activity get their own and
   the rest fold into "Other". The fold is computed from the DATA, not hardcoded,
   so if RCx ever becomes common it earns a column on its own. */
/* Header text, not the amounts, is what sizes a department column: "CONSTRUCTION" is
   12 uppercase chars against figures of about 9. Abbreviating the two longest bought
   the last 9px the table was over by. Full name stays in the column tooltip. */
const SHORT_LABEL = { Construction: 'Constr.', 'E3 Lighting': 'Lighting', 'Perf Mgmt': 'Perf' };
const shortOf = (label) => SHORT_LABEL[label] || label;

const MAX_DEPT_COLS = 5;
const DEPT_W = 84;

/* ⚠️ These widths were WRONG on the first pass. I added them up by hand, dropped the
   Unalloc column from the sum, and shipped a table needing 1256px into the ~1174 a row
   actually gets — so Current and Delta GM were off-screen. Exactly the defect fixed on
   Job Costs this morning, made again the same way: by trusting arithmetic instead of
   measuring. TABLE_MIN is now DERIVED from these values (1152), so the minWidth and the
   columns cannot drift apart, and the rendered widths were checked in the browser.

   ⚠️ SECOND lesson from the same table: these w values are only a HINT. The table is
   table-layout:auto, so a nowrap cell wider than its declared width expands the column
   regardless -- "$19,726,152" wants ~110px against a declared 96. That is why the first
   trim still clipped by 92px. What actually bought the space was cell PADDING (10px ->
   6px across 13 columns = ~104px) plus a half-point off the mono size. Measure the
   rendered scrollWidth; never trust these numbers as the real total. */
const W = { proj: 196, contract: 96, other: 76, unalloc: 86, overall: 96, gm: 62, delta: 58 };
// Real total, computed not guessed. A row gets content-38 ~= 1174px at 1512.
const TABLE_MIN = W.proj + W.contract + MAX_DEPT_COLS * DEPT_W + W.other + W.unalloc
  + W.overall + W.gm * 2 + W.delta;

const Th = ({ children, w, align = 'right', title, tone, className }) => (
  <th
    title={title}
    className={className}
    style={{
      width: w, minWidth: w, padding: '8px 5px', textAlign: align, whiteSpace: 'nowrap',
      fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase',
      color: tone || 'var(--text-3)', background: 'var(--bg-surface-2)',
      borderBottom: '1px solid var(--border-default)',
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = 'right', mono = true, color, w, title, weight, className }) => (
  <td
    title={title}
    className={[mono ? 'mono' : '', className || ''].filter(Boolean).join(' ') || undefined}
    style={{
      width: w, padding: '8px 5px', textAlign: align, whiteSpace: 'nowrap', verticalAlign: 'top',
      fontSize: mono ? 12.5 : 13, fontWeight: weight || (mono ? 600 : 400),
      fontVariantNumeric: 'tabular-nums', letterSpacing: mono ? '-0.01em' : undefined,
      color: color || 'var(--text-1)', borderBottom: '1px solid var(--border-subtle)',
    }}
  >
    {children}
  </td>
);

/* The header band over the column headers, naming the groups (as the workbook's merged headers do). */
const GroupTh = ({ children, span, className }) => (
  <th
    colSpan={span}
    className={className}
    style={{
      padding: '6px 5px 4px', textAlign: 'center', whiteSpace: 'nowrap',
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      color: 'var(--text-4)', background: 'var(--bg-surface)',
      borderBottom: children ? '1px solid var(--border-subtle)' : 'none',
    }}
  >
    {children}
  </th>
);

/* Department columns boxed like the workbook: the first gets the heavier group rule, the
   rest a thin one, and every other department is shaded (body rows only). */
const deptRule = (i) => (i === 0 ? 'ds-gr' : 'ds-vr');
const deptCls = (i) => `${deptRule(i)}${i % 2 === 1 ? ' ds-band' : ''}`;
const cx = (...names) => names.filter(Boolean).join(' ') || undefined;

/* A variance figure with its % underneath, highlighted by the workbook's rule. */
const VarCell = ({ w, value, pct, limit, weight, noBase, noBaseLabel = 'no budget', cls }) => {
  const f = flagOf(pct, limit);
  return (
    <Td w={w} weight={weight} className={cx(cls, f && `ds-${f}`)}>
      {value === 0 ? '—' : fmt$(value)}
      {value !== 0 && (
        <span className="ds-sub" style={f ? { color: 'inherit' } : undefined}>{noBase ? noBaseLabel : fmtSignedPct(pct)}</span>
      )}
    </Td>
  );
};

const DepartmentStatusPage = ({ onOpenProject }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('gm');
  const [filterHealth, setFilterHealth] = useState('all');
  const [filterPM, setFilterPM] = useState('');
  const [filterCM, setFilterCM] = useState('');
  const [infoAnchor, setInfoAnchor] = useState(null); // the ⓘ pop-up, anchored to its button

  const load = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      setData(await getDeptStatus());
    } catch (e) {
      console.error('Department status fetch failed:', e);
      setError(e.message || 'Failed to load the department status report');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data && data.rows) || [];
  const departments = (data && data.departments) || [];
  // Archived jobs come back flagged; they show only under the Archived filter.
  const activeRows = useMemo(() => rows.filter((r) => !r.archived), [rows]);
  const archivedRows = useMemo(() => rows.filter((r) => r.archived), [rows]);

  /* Which departments earn a column: the ones with the most jobs showing activity.
     Everything else folds into "Other", with the folded names in its tooltip so the
     number is never anonymous. */
  const { cols, folded } = useMemo(() => {
    const counts = {};
    departments.forEach((d) => { counts[d.phase] = 0; });
    activeRows.forEach((r) => departments.forEach((d) => {
      if ((r.departments[d.phase] || 0) !== 0) counts[d.phase] += 1;
    }));
    const ranked = [...departments].sort((a, b) => counts[b.phase] - counts[a.phase]);
    const keep = ranked.slice(0, MAX_DEPT_COLS)
      .filter((d) => counts[d.phase] > 0)
      // put them back in phase order so the columns read like the workbook
      .sort((a, b) => Number(a.phase) - Number(b.phase));
    const keepSet = new Set(keep.map((d) => d.phase));
    return { cols: keep, folded: departments.filter((d) => !keepSet.has(d.phase)) };
  }, [activeRows, departments]);

  const otherOf = useCallback(
    (r) => folded.reduce((s, d) => s + (r.departments[d.phase] || 0), 0),
    [folded],
  );
  const otherBudgetOf = useCallback(
    (r) => folded.reduce((s, d) => s + ((r.dept_budgets && r.dept_budgets[d.phase]) || 0), 0),
    [folded],
  );

  // People filters, as on Job Costs: a job matches its project's PMs (can be several) and CM.
  const peopleOptions = useCallback((field, all) => {
    const set = new Set();
    rows.forEach((r) => (r[field] || '').split(',').map((x) => x.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return [{ value: '', label: all }, ...[...set].sort().map((n) => ({ value: n, label: n }))];
  }, [rows]);
  const pmOptions = useMemo(() => peopleOptions('project_manager', 'All PMs'), [peopleOptions]);
  const cmOptions = useMemo(() => peopleOptions('construction_manager', 'All CMs'), [peopleOptions]);
  const matchesPeople = useCallback((r) => {
    if (filterPM && !(r.project_manager || '').split(',').map((x) => x.trim()).includes(filterPM)) return false;
    if (filterCM && !(r.construction_manager || '').split(',').map((x) => x.trim()).includes(filterCM)) return false;
    return true;
  }, [filterPM, filterCM]);
  // The top bar's search (lib/pageSearch): job number, name, PM, CM. With the people filters, so the
  // status counts, the rows and the totals follow it.
  const search = usePageSearch('Search BoMoCo…');
  const matchesText = useCallback((r) => matchesSearch(search, r.job_id, r.project_number, r.project_name,
    r.project_manager, r.construction_manager), [search]);
  const peopleActive = useMemo(() => activeRows.filter((r) => matchesPeople(r) && matchesText(r)), [activeRows, matchesPeople, matchesText]);
  const peopleArchived = useMemo(() => archivedRows.filter((r) => matchesPeople(r) && matchesText(r)), [archivedRows, matchesPeople, matchesText]);

  const visible = useMemo(() => {
    let out;
    if (filterHealth === 'archived') out = peopleArchived;
    else if (filterHealth === 'all') out = peopleActive;
    else out = peopleActive.filter((r) => filterBucket(ratioOf(r)) === filterHealth);
    const s = [...out];
    const nullsLast = (a, b, f) => (a == null ? (b == null ? 0 : 1) : b == null ? -1 : f(a, b));
    if (sortBy === 'gm') s.sort((a, b) => nullsLast(a.gm_delta, b.gm_delta, (x, y) => x - y));
    else if (sortBy === 'overrun') s.sort((a, b) => (healthRank(a) - healthRank(b)) || ((b.cost - b.budget) - (a.cost - a.budget)));
    else if (sortBy === 'budget') s.sort((a, b) => b.budget - a.budget);
    else if (sortBy === 'spent') s.sort((a, b) => (ratioOf(b) || 0) - (ratioOf(a) || 0));
    else if (sortBy === 'gm-high') s.sort((a, b) => nullsLast(a.current_gm, b.current_gm, (x, y) => y - x));
    else if (sortBy === 'gm-low') s.sort((a, b) => nullsLast(a.current_gm, b.current_gm, (x, y) => x - y));
    else if (sortBy === 'unallocated') s.sort((a, b) => Math.abs(b.unallocated) - Math.abs(a.unallocated));
    else if (sortBy === 'contract') s.sort((a, b) => b.contract - a.contract);
    else s.sort((a, b) => String(a.project_name).localeCompare(String(b.project_name)));
    return s;
  }, [peopleActive, peopleArchived, filterHealth, sortBy]);

  /* Totals over the VISIBLE set, so the footer always reflects the filter. GM is
     revenue-weighted (total margin ÷ total contract) over contract-bearing jobs
     only — never a mean of per-project percentages. Same rule as Job Costs. */
  const totals = useMemo(() => {
    const t = { contract: 0, overall: 0, unallocated: 0, other: 0, depts: {}, invoiced: 0, invoicedOf: 0 };
    cols.forEach((d) => { t.depts[d.phase] = 0; });
    let cBudget = 0, cBase = 0;
    visible.forEach((r) => {
      t.contract += r.contract; t.overall += r.overall; t.unallocated += r.unallocated;
      t.other += otherOf(r);
      cols.forEach((d) => { t.depts[d.phase] += r.departments[d.phase] || 0; });
      if (r.contract > 0) { cBudget += r.budget; cBase += r.cost + (r.committed || 0); }
      // % billed over the jobs that have both a contract and CE's invoiced figure.
      if (r.invoiced != null && r.contract > 0) { t.invoiced += r.invoiced; t.invoicedOf += r.contract; }
    });
    t.booked = t.contract > 0 ? ((t.contract - cBudget) / t.contract) * 100 : null;
    t.current = t.contract > 0 ? ((t.contract - cBase) / t.contract) * 100 : null;
    t.delta = t.booked == null ? null : t.current - t.booked;
    return t;
  }, [visible, cols, otherOf]);

  // Button counts follow the people filters (but not the status itself), as on Job Costs.
  const counts = useMemo(() => ({
    all: peopleActive.length,
    over: peopleActive.filter((r) => filterBucket(ratioOf(r)) === 'over').length,
    'at-risk': peopleActive.filter((r) => filterBucket(ratioOf(r)) === 'at-risk').length,
    'on-track': peopleActive.filter((r) => filterBucket(ratioOf(r)) === 'on-track').length,
    archived: peopleArchived.length,
  }), [peopleActive, peopleArchived]);
  const showBilled = rows.some((r) => r.invoiced != null); // AR access (the server sends null without it)

  if (loading) return <LoadingState message="Loading department status…" />;
  if (error) {
    return (
      <StateScreen
        kind="server-error"
        title="Couldn’t load the department status report"
        body="Something went wrong reading the job-cost data. Check your connection and try again."
        primary={{ label: 'Retry', onClick: load }}
      />
    );
  }
  if (!rows.length) {
    return (
      <StateScreen
        kind="empty"
        title="No job-cost data yet"
        body="Once ComputerEase job costs sync for the active roster, budget variance by department will appear here."
      />
    );
  }

  const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 32, width: 32, padding: 0, borderRadius: 8, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', flex: '0 0 auto' };
  // How to read the page, on demand (Duncan 2026-10-10: most people already know it, so it
  // shouldn't sit on screen all the time). It used to be a note under the filter bar.
  const infoBtn = (
    <button
      type="button"
      className="ds-refresh"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => setInfoAnchor(infoAnchor ? null : e.currentTarget.getBoundingClientRect())}
      title="How to read this page"
      aria-label="How to read this page"
      aria-expanded={!!infoAnchor}
      style={{ ...iconBtn, ...(infoAnchor ? { color: 'var(--accent-text)', background: 'var(--accent-soft)' } : {}) }}
    >
      <InfoIcon />
    </button>
  );
  // Job Costs' Refresh button, icon-only in the bar.
  const refreshBtn = (
    <button
      type="button"
      className="ds-refresh"
      onClick={load}
      title="Refresh department status"
      aria-label="Refresh department status"
      style={iconBtn}
    >
      <JcIcon name="refresh" size={13} />
    </button>
  );

  return (
    // The page fills the window (56px app header + 24px padding above and below) so the table
    // scrolls inside its own frame and the filter bar + column labels stay in view (Duncan
    // 2026-10-15: "freeze when you scroll down so you can always see them"). The frame takes
    // whatever height the filter bar leaves, however many lines it wraps to.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: 'calc(100vh - 104px)', minHeight: 360 }}>
      <style>{`
        .ds-plink { background: none; border: none; padding: 0; cursor: pointer; font: inherit;
                    color: inherit; text-align: left; }
        .ds-plink:hover { color: var(--accent-text); }
        @media (min-width: 1340px) { .ds-th, .ds-td { white-space: nowrap; } }
        /* The second line under a figure: its %, % billed, or the contract date. */
        .ds-sub { display: block; margin-top: 2px; font-size: 11px; font-weight: 500; color: var(--text-3); }
        .ds-sort:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .ds-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        /* Five buttons outgrow a phone: there the row scrolls sideways (as on Job Costs). */
        .ds-health { display: flex; max-width: 100%; overflow-x: auto; scrollbar-width: none; }
        .ds-health::-webkit-scrollbar { display: none; }
        .ds-health [role="tab"] { white-space: nowrap; }
        /* Department columns boxed like the monthly workbook (Duncan 2026-10-10: "not enough
           visual separation between the department columns"): thin rules between departments,
           alternate departments shaded, heavier rules between the three groups (job | departments |
           margin) and above the totals. Inset shadows, not borders: they take no width (the table
           fills a 1512 laptop exactly) and the old dark theme doesn't reset them. */
        .ds-table .ds-vr { box-shadow: inset 1px 0 0 var(--border-strong); }
        .ds-table .ds-gr { box-shadow: inset 2px 0 0 var(--border-strong); }
        .ds-table tr.ds-total td { box-shadow: inset 0 2px 0 var(--border-strong); }
        .ds-table tr.ds-total td.ds-vr { box-shadow: inset 1px 0 0 var(--border-strong), inset 0 2px 0 var(--border-strong); }
        .ds-table tr.ds-total td.ds-gr { box-shadow: inset 2px 0 0 var(--border-strong), inset 0 2px 0 var(--border-strong); }
        /* Backgrounds and colours need !important and a longer selector: the old dark theme forces
           every table cell to one grey with no background (App.css: .app.dark .app-main td,
           !important). Order matters: a hovered row beats the shading, the red fill beats both. */
        .app .app-main .ds-table td.ds-band { background: var(--bg-surface-2) !important; }
        .app .app-main .ds-table tr.ds-row:hover td { background: var(--bg-hover) !important; }
        .app .app-main .ds-table tr.ds-row td.ds-bad { background: var(--danger-soft) !important; color: var(--danger) !important; }
        .app .app-main .ds-table tr.ds-row td.ds-good { color: var(--success) !important; }
        .app .app-main .ds-table td.ds-warn { color: var(--warning) !important; }
      `}</style>

      {/* Filter bar — the Job Costs bar (Duncan 2026-10-10: "the same filtering on the top
          that we have for the job cost tab"): status, PM, CM, Sort by, Refresh. */}
      <section style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        {infoBtn /* first in the bar (Duncan 2026-10-10) */}
        <div className="ds-health">
          <SegmentedControl
            value={filterHealth}
            onChange={setFilterHealth}
            options={[
              { value: 'all', label: 'All', count: counts.all },
              { value: 'over', label: 'Over Budget', count: counts.over },
              { value: 'at-risk', label: 'At Risk', count: counts['at-risk'] },
              { value: 'on-track', label: 'On Track', count: counts['on-track'] },
              { value: 'archived', label: 'Archived', count: counts.archived },
            ]}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', flexWrap: 'wrap' }}>
          <SortSelect value={filterPM} onChange={setFilterPM} options={pmOptions} />
          <SortSelect value={filterCM} onChange={setFilterCM} options={cmOptions} />
          {(filterPM || filterCM) && (
            <button
              type="button"
              onClick={() => { setFilterPM(''); setFilterCM(''); }}
              style={{ height: 32, padding: '0 11px', borderRadius: 8, fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 8 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: '0 0 auto' }}>
          <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort by</span>
          <SortSelect value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
          {refreshBtn}
        </div>
      </section>

      {/* The basis note, now behind the ⓘ button. Duncan asked for it originally: this report
          intentionally reads a different aggregate than the Job Costs tab, and two Finance screens
          disagreeing by design needs saying on the screen, not just in a commit. */}
      <Popover open={!!infoAnchor} anchor={infoAnchor} onClose={() => setInfoAnchor(null)} align="left" minWidth={380}>
        <PopoverHeader title="How to read this page" />
        <div style={{ maxWidth: 420, padding: '2px 8px 6px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-2)', whiteSpace: 'normal' }}>
          <p style={{ margin: 0 }}>
            Departments are ComputerEase <strong>phases</strong>. Amounts are budget variance, under budget in
            parentheses, with the % of that department's own budget underneath (Overall: % of the contract).
          </p>
          <p style={{ margin: 0 }}>
            Highlighted like the monthly workbook: <strong>red</strong> when a department is more than 10% over its
            budget, the job more than 2% of its contract over, or margin down more than 2 points; <strong>green</strong> for
            the same amounts under or up.
          </p>
          {showBilled && <p style={{ margin: 0 }}><strong>% billed</strong> is what CE has invoiced to date.</p>}
          <p style={{ margin: 0 }}>
            <strong>Unallocated</strong> is cost in the job total that no phase row accounts for; the spreadsheet this
            replaces folded it into Construction.
          </p>
          {folded.length > 0 && <p style={{ margin: 0 }}><strong>Other</strong> combines {folded.map((d) => d.label).join(', ')}.</p>}
        </div>
      </Popover>

      {visible.length === 0 && search.trim() ? (
        <StateScreen kind="empty" {...noMatchText(search)} />
      ) : visible.length === 0 ? (
        filterHealth === 'archived'
          ? <StateScreen kind="empty" title="No archived jobs" body="Jobs archived from Job Costs or the Projects page show here with their department figures." />
          : <StateScreen kind="empty" title="No jobs match this filter" body="Try a different filter, or switch back to All." />
      ) : (
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: '0 1 auto', minHeight: 0 }}>
        <div style={{ overflow: 'auto', flex: '0 1 auto', minHeight: 0 }}>
          <table className="ds-table" style={{ width: '100%', minWidth: TABLE_MIN, borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <GroupTh span={2} />
                <GroupTh span={cols.length + (folded.length > 0 ? 1 : 0) + 2} className="ds-gr">Budget variance by department · % of each budget</GroupTh>
                <GroupTh span={3} className="ds-gr">Margin</GroupTh>
              </tr>
              <tr>
                <Th align="left" w={W.proj}>Project</Th>
                <Th w={W.contract}>Contract</Th>
                {cols.map((d, i) => <Th key={d.phase} w={DEPT_W} align="center" className={deptRule(i)} title={`${d.label} — CE phase ${d.phase}`}>{shortOf(d.label)}</Th>)}
                {folded.length > 0 && (
                  <Th w={W.other} align="center" className={deptRule(cols.length)} title={`Combines: ${folded.map((f) => `${f.label} (phase ${f.phase})`).join(', ')}`}>Other</Th>
                )}
                <Th w={W.unalloc} className="ds-vr" title="Cost in the job total that no phase row accounts for">Unalloc.</Th>
                <Th w={W.overall} className="ds-vr">Overall</Th>
                <Th w={W.gm} className="ds-gr" title="(contract − budget) ÷ contract">Booked</Th>
                <Th w={W.gm} title="(contract − cost − committed) ÷ contract">Current</Th>
                <Th w={W.delta} title="Current − Booked, in percentage points">Δ GM</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const other = otherOf(r);
                const otherBudget = otherBudgetOf(r);
                const signed = fmtShortDate(r.contract_date);
                const billed = r.invoiced != null && r.contract > 0 ? Math.round((r.invoiced / r.contract) * 100) : null;
                const gmFlag = flagOf(r.gm_delta == null ? null : -r.gm_delta, GM_FLAG_PTS);
                return (
                  <tr key={r.job_id} className="ds-row">
                    <Td align="left" mono={false} w={W.proj}>
                      <button
                        type="button"
                        className="ds-plink"
                        onClick={() => onOpenProject && onOpenProject(r.slug || r.job_id)}
                        title={r.project_manager ? `PM: ${r.project_manager}` : undefined}
                      >
                        <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W.proj - 20 }}>{r.project_name}</span>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W.proj - 10, fontSize: 11.5, color: 'var(--text-3)' }}>
                          <span className="mono">#{r.project_number}</span>
                          {signed
                            ? <span title="Contract date"> · signed {signed}</span>
                            : <span style={{ color: 'var(--text-4)' }}> · no contract date</span>}
                        </span>
                      </button>
                    </Td>
                    <Td w={W.contract}>
                      {fmt$full(r.contract)}
                      {billed != null && <span className="ds-sub">{billed}% billed</span>}
                    </Td>
                    {cols.map((d, i) => {
                      const v = r.departments[d.phase] || 0;
                      const b = (r.dept_budgets && r.dept_budgets[d.phase]) || 0;
                      return <VarCell key={d.phase} w={DEPT_W} cls={deptCls(i)} value={v} pct={pctOf(v, b)} limit={DEPT_FLAG_PCT} noBase={!(b > 0)} />;
                    })}
                    {folded.length > 0 && (
                      <VarCell w={W.other} cls={deptCls(cols.length)} value={other} pct={pctOf(other, otherBudget)} limit={DEPT_FLAG_PCT} noBase={!(otherBudget > 0)} />
                    )}
                    <Td w={W.unalloc} color={Math.abs(r.unallocated) > 0.5 ? 'var(--warning)' : 'var(--text-4)'} className={cx('ds-vr', Math.abs(r.unallocated) > 0.5 && 'ds-warn')}>
                      {Math.abs(r.unallocated) < 0.5 ? '—' : fmt$(r.unallocated)}
                    </Td>
                    <VarCell w={W.overall} cls="ds-vr" value={r.overall} pct={pctOf(r.overall, r.contract)} limit={OVERALL_FLAG_PCT} weight={700} noBase={!(r.contract > 0)} noBaseLabel="no contract" />
                    <Td w={W.gm} color="var(--text-2)" className="ds-gr">{fmtPct(r.booked_gm)}</Td>
                    <Td w={W.gm} color="var(--text-2)">{fmtPct(r.current_gm)}</Td>
                    <Td w={W.delta} weight={700} color="var(--text-2)" className={gmFlag ? `ds-${gmFlag}` : undefined}>{fmtPts(r.gm_delta)}</Td>
                  </tr>
                );
              })}
              <tr className="ds-total">
                <Td align="left" mono={false} w={W.proj} weight={700}>
                  {visible.length} {visible.length === 1 ? 'job' : 'jobs'}
                </Td>
                <Td w={W.contract} weight={700}>
                  {fmt$full(totals.contract)}
                  {totals.invoicedOf > 0 && <span className="ds-sub">{Math.round((totals.invoiced / totals.invoicedOf) * 100)}% billed</span>}
                </Td>
                {cols.map((d, i) => <Td key={d.phase} w={DEPT_W} weight={700} className={deptCls(i)}>{fmt$(totals.depts[d.phase])}</Td>)}
                {folded.length > 0 && <Td w={W.other} weight={700} className={deptCls(cols.length)}>{fmt$(totals.other)}</Td>}
                <Td w={W.unalloc} weight={700} color="var(--warning)" className="ds-vr ds-warn">{fmt$(totals.unallocated)}</Td>
                <Td w={W.overall} weight={700} className="ds-vr">
                  {fmt$(totals.overall)}
                  {totals.contract > 0 && <span className="ds-sub">{fmtSignedPct(pctOf(totals.overall, totals.contract))}</span>}
                </Td>
                <Td w={W.gm} weight={700} color="var(--text-2)" className="ds-gr">{fmtPct(totals.booked)}</Td>
                <Td w={W.gm} weight={700} color="var(--text-2)">{fmtPct(totals.current)}</Td>
                <Td w={W.delta} weight={700} color="var(--text-2)">{fmtPts(totals.delta)}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default DepartmentStatusPage;
